import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { getCategoryTypeFromString } from '@modules/common/utils';
import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  MenuProps,
  Select,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import { Category } from 'generated/graphql';
import { capitalize } from 'lodash';
import { ReactNode } from 'react';

interface SelectCategoryMenuProps {
  value: Category | undefined;
  onChange: (value: Category) => void;
  sxProps?: SxProps<Theme>;
  shouldShowLabel?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  label?: ReactNode;
  menuProps?: Omit<MenuProps, 'open'>;
}

export const SelectCategoryMenu = ({
  value,
  onChange,
  sxProps,
  shouldShowLabel = false,
  helperText,
  size = 'small',
  disabled = false,
  label = 'Select Category',
  menuProps,
}: SelectCategoryMenuProps) => {
  const renderCategorySelectMenuItem = (category: Category) => {
    return (
      <Stack spacing={1} direction="row" alignItems="center">
        <Box>
          <CategoryIcon category={getCategoryTypeFromString(category)} />
        </Box>
        <Typography variant="body2">{capitalize(category)}</Typography>
      </Stack>
    );
  };

  return (
    <FormControl
      sx={sxProps}
      size={size}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
    >
      {shouldShowLabel && (
        <InputLabel id="category-select-label">{label}</InputLabel>
      )}
      <Select
        labelId="category-select-label"
        id="category-select-label"
        value={value || ''}
        label={shouldShowLabel ? label : undefined}
        onChange={(event) => onChange(event.target.value as Category)}
        size={size}
        MenuProps={{
          disablePortal: true,
          sx: {
            zIndex: 3000,
          },
          ...menuProps,
        }}
        renderValue={(category: Category) =>
          shouldShowLabel ? (
            renderCategorySelectMenuItem(category)
          ) : (
            <CategoryIcon category={getCategoryTypeFromString(category)} />
          )
        }
        variant="outlined"
      >
        <MenuItem value={Category.Help}>
          {renderCategorySelectMenuItem(Category.Help)}
        </MenuItem>
        <MenuItem value={Category.Improvement}>
          {renderCategorySelectMenuItem(Category.Improvement)}
        </MenuItem>
        <MenuItem value={Category.Praise}>
          {renderCategorySelectMenuItem(Category.Praise)}
        </MenuItem>
        <MenuItem value={Category.Issue}>
          {renderCategorySelectMenuItem(Category.Issue)}
        </MenuItem>
      </Select>
      {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
